import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Stack, useTheme } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import moment from "moment";

import Button from "components/basic/button";
import Typography from "components/basic/typography";
import AttachmentList from "components/basic/attachmentList";

import { EVENT_TYPE_MAP } from "../../../calendar/eventTypes";
import { AvailabilityData } from "../../../calendar/setAvailability/types";
import { Booking, LeadCustomer } from "../../../calendar/booking/types";
import QuoteDetailsDialog from "../../../calendar/booking/components/QuoteDetailsDialog";
import StatusChip from "../StatusChip";
import { ConfirmInput } from "../../meetingsApi";
import { Meeting } from "../../types";
import { isValidUrl } from "./helpers/isValidUrl";
import RequestedDetails from "./components/RequestedDetails";
import CustomerResponseCard from "./components/CustomerResponseCard";
import ConfirmedLinkActions from "./components/ConfirmedLinkActions";
import ConfirmPanel from "./components/ConfirmPanel";
import MeetingSummaryRail from "./components/MeetingSummaryRail";
import { useMessageNavigation } from "../../../message/messagesApi";

interface MeetingDetailProps {
  meeting: Meeting;
  availability: AvailabilityData | null | undefined;
  bookings: Booking[];
  leadCustomer?: LeadCustomer;
  onBack: () => void;
  onConfirm: (id: string, input: ConfirmInput) => void;
  onCancel: (id: string, note?: string) => void;
}

const MeetingDetail: React.FC<MeetingDetailProps> = ({
  meeting,
  availability,
  bookings,
  leadCustomer,
  onBack,
  onConfirm,
  onCancel,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("meetings");
  const messageCounterpart = useMessageNavigation();
  const meta = EVENT_TYPE_MAP[meeting.type];
  const isVideo = meeting.type === "video";
  const isPending = meeting.displayStatus === "pending";
  const isConfirmed = meeting.displayStatus === "confirmed";
  const readOnly = !isPending && !isConfirmed;

  const [editing, setEditing] = useState(isPending);
  const [proposing, setProposing] = useState(false);
  const [date, setDate] = useState(meeting.date);
  const [from, setFrom] = useState(meeting.from);
  const [to, setTo] = useState(meeting.to);
  const [link, setLink] = useState(meeting.meetingLink ?? "");
  const [note, setNote] = useState("");
  const [viewingQuotes, setViewingQuotes] = useState(false);

  const durationMinutes = Math.max(
    moment(meeting.to, "HH:mm").diff(moment(meeting.from, "HH:mm"), "minutes"),
    30,
  );

  const linkValid = isValidUrl(link);
  const confirmDisabled = (isVideo && !linkValid) || (proposing && !from);

  const handleConfirm = () => {
    const input: ConfirmInput = {
      meetingLink: link.trim() || undefined,
      note: note.trim() || undefined,
      ...(proposing ? { date, from, to } : {}),
    };
    onConfirm(meeting.id, input);
  };

  // Prefill the propose-new-time panel with the customer's proposed slot.
  const useProposedTime = () => {
    if (meeting.proposedDate && meeting.proposedFrom && meeting.proposedTo) {
      setDate(meeting.proposedDate);
      setFrom(meeting.proposedFrom);
      setTo(meeting.proposedTo);
      setProposing(true);
      setEditing(true);
    }
  };

  const requestedWhen = `${moment(meeting.date, "YYYY-MM-DD").format(
    "dddd, MMM D, YYYY",
  )} · ${t("detail.et", { time: moment(meeting.from, "HH:mm").format("h:mm A") })}`;

  return (
    <>
      <Box sx={{ height: "100%", overflowY: "auto" }}>
        {/* Top bar */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ p: 1 }}
        >
          <Button
            variant="text"
            startAdornment={<ArrowBackIcon />}
            onClick={onBack}
          >
            {t("detail.back")}
          </Button>
        </Stack>

        <Box sx={{ px: 2, pb: 3 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ mb: 0.5 }}
          >
            <Typography variant="h3" style={{ fontWeight: 700 }}>
              {isVideo ? t("detail.video_request") : meta.label}
            </Typography>
            <StatusChip status={meeting.displayStatus} />
          </Stack>
          <Typography
            variant="body2"
            style={{ color: theme.palette.text.secondary, marginBottom: 16 }}
          >
            {isVideo
              ? t("detail.video_requested", { name: meeting.customerName })
              : `${meeting.customerName} · ${meeting.title}`}
          </Typography>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems="flex-start"
          >
            {/* Main column */}
            <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
              <RequestedDetails meeting={meeting} />

              {meeting.booking.attachments.length > 0 && (
                <Box
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    p: 2,
                    mb: 2,
                  }}
                >
                  <AttachmentList attachments={meeting.booking.attachments} />
                </Box>
              )}

              <CustomerResponseCard
                meeting={meeting}
                onUseProposedTime={useProposedTime}
              />

              {/* What happens next (pending only) */}
              {isPending && (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    backgroundColor: `${theme.palette.info.main}10`,
                    borderRadius: 1,
                    p: 1.5,
                    mb: 2,
                  }}
                >
                  <InfoOutlinedIcon
                    fontSize="small"
                    sx={{ color: theme.palette.info.main, mt: "2px" }}
                  />
                  <Box>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      {t("detail.next_title")}
                    </Typography>
                    <Typography
                      variant="caption"
                      style={{ color: theme.palette.text.secondary }}
                    >
                      {t("detail.next_body")}
                    </Typography>
                  </Box>
                </Stack>
              )}

              {isConfirmed && !editing && (
                <ConfirmedLinkActions
                  meeting={meeting}
                  onReschedule={() => {
                    setEditing(true);
                    setProposing(true);
                  }}
                  onCancel={() =>
                    onCancel(meeting.id, note.trim() || undefined)
                  }
                />
              )}

              {editing && (
                <ConfirmPanel
                  meeting={meeting}
                  availability={availability}
                  bookings={bookings}
                  isVideo={isVideo}
                  isPending={isPending}
                  proposing={proposing}
                  date={date}
                  from={from}
                  link={link}
                  note={note}
                  durationMinutes={durationMinutes}
                  linkValid={linkValid}
                  confirmDisabled={confirmDisabled}
                  requestedWhen={requestedWhen}
                  onToggleProposing={() => setProposing((p) => !p)}
                  onDateChange={(d) => {
                    setDate(d);
                    setFrom("");
                    setTo("");
                  }}
                  onSlotSelect={(f, t) => {
                    setFrom(f);
                    setTo(t);
                  }}
                  onLinkChange={setLink}
                  onNoteChange={setNote}
                  onConfirm={handleConfirm}
                  onClose={() => {
                    setEditing(false);
                    setProposing(false);
                  }}
                  onDecline={() =>
                    onCancel(meeting.id, note.trim() || undefined)
                  }
                />
              )}

              {readOnly && meeting.vendorNote && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" style={{ fontWeight: 600 }}>
                    {t("detail.note")}
                  </Typography>
                  <Typography variant="body2">{meeting.vendorNote}</Typography>
                </Box>
              )}
            </Box>

            <MeetingSummaryRail
              meeting={meeting}
              leadCustomer={leadCustomer}
              onViewQuotes={() => setViewingQuotes(true)}
              onMessage={
                leadCustomer?.accountId
                  ? () => messageCounterpart(leadCustomer.accountId as number)
                  : undefined
              }
            />
          </Stack>
        </Box>
      </Box>

      {viewingQuotes && leadCustomer && (
        <QuoteDetailsDialog
          customer={leadCustomer}
          onClose={() => setViewingQuotes(false)}
        />
      )}
    </>
  );
};

export default MeetingDetail;
